import { ref } from 'vue';

// Module-level singleton — shared across all composable calls so Toolbar can
// write and pages/composables can read without prop-drilling.
const navigationTargets = ref([]);

/**
 * Provides the shared navigation targets registry.
 *
 * - The Toolbar calls `registerNavigationTargets(tabData)` whenever its computed
 *   tab list changes (tabs become available/unavailable as scenario steps complete).
 * - `useAiAgentActions` reads `getAvailableNavigationTargets()` internally at send-time
 *   and injects the route list into the `navigate` tool's description schema.
 *   Pages do not need to call or expose this themselves.
 * - `clearNavigationTargets` is called by Toolbar on unmount so stale data isn't left behind.
 */
export function useToolbarNavigation() {
  /**
   * Register navigation targets. Accepts a pre-computed flat array of
   * { routeName, available } entries (produced by Toolbar's allNavigationTargets computed).
   * @param {Array<{ routeName: string, available: boolean }>} targets
   */
  function registerNavigationTargets(targets) {
    navigationTargets.value = targets.map(t => ({
      routeName: t.routeName,
      label: t.label || t.routeName,
      available: !!t.available,
    }));
  }

  /**
   * Clear all targets — called when Toolbar unmounts so stale data isn't left behind.
   */
  function clearNavigationTargets() {
    navigationTargets.value = [];
  }

  /**
   * Returns all registered targets (available and unavailable).
   */
  function getNavigationTargets() {
    return navigationTargets.value;
  }

  /**
   * Returns only the targets the user can currently navigate to.
   */
  function getAvailableNavigationTargets() {
    return navigationTargets.value.filter(t => t.available);
  }

  return {
    navigationTargets,
    registerNavigationTargets,
    clearNavigationTargets,
    getNavigationTargets,
    getAvailableNavigationTargets,
  };
}
